import { readonly, ref } from 'vue';
import type { App } from 'vue';
import messages from './messages.json';

export type AppLocale = 'id' | 'en' | 'ms';
export const LANGUAGE_STORAGE_KEY = 'radfast-language';
export const languages: ReadonlyArray<{ code: AppLocale; name: string; short: string }> = [
  { code: 'id', name: 'Indonesia', short: 'ID' },
  { code: 'en', name: 'English', short: 'EN' },
  { code: 'ms', name: 'Melayu', short: 'MS' },
];
const activeLocale = ref<AppLocale>('id');
const catalog: Record<string, { en: string; ms: string }> = messages;

export function isAppLocale(value: unknown): value is AppLocale {
  return value === 'id' || value === 'en' || value === 'ms';
}

export function setLocale(value: unknown, persist = true): void {
  if (!isAppLocale(value)) return;
  activeLocale.value = value;
  if (typeof document !== 'undefined') document.documentElement.lang = value;
  if (persist) {
    try { localStorage.setItem(LANGUAGE_STORAGE_KEY, value); } catch { /* Usable without browser storage. */ }
  }
}

export function initializeLocale(): () => void {
  let stored: unknown;
  try { stored = localStorage.getItem(LANGUAGE_STORAGE_KEY); } catch { /* Default to Indonesian. */ }
  setLocale(isAppLocale(stored) ? stored : 'id', false);
  const synchronize = (event: StorageEvent) => {
    if (event.key === LANGUAGE_STORAGE_KEY || event.key === null) {
      setLocale(isAppLocale(event.newValue) ? event.newValue : 'id', false);
    }
  };
  window.addEventListener('storage', synchronize);
  return () => window.removeEventListener('storage', synchronize);
}

// Translate authored UI messages only. Customer data, credentials, scripts,
// identifiers, amounts and API request values must never pass through here.
export function t(source: string, params: Record<string, string | number> = {}): string {
  const key = source.replace(/\s+/g, ' ').trim();
  const language = activeLocale.value;
  const entry = Object.prototype.hasOwnProperty.call(catalog, key) ? catalog[key] : undefined;
  const message = language === 'id' ? source : entry?.[language] || source;
  return message.replace(/\{(\w+)\}/g, (placeholder, name: string) =>
    Object.prototype.hasOwnProperty.call(params, name) ? String(params[name]) : placeholder);
}

export function useAppLocale() {
  return { locale: readonly(activeLocale), languages, setLocale, t };
}

export function installI18n(app: App): void {
  app.config.globalProperties.$t = t;
}

declare module '@vue/runtime-core' {
  interface ComponentCustomProperties { $t: typeof t }
}
